import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { Parameter } from '../../../models/parameter.model';
import { ParameterService } from '../../../services/parameter.service';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';

@Component({
  selector: 'app-parameter-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    ProgressSpinnerModule,
    ToolbarModule,
    ToastModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
  ],
  templateUrl: './parameter-update.component.html',
})
export class ParameterUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private formGroupService = inject(FormGroupService);
  private parameterService = inject(ParameterService);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    key: [{ value: null, disabled: true }, [Validators.required]],
    value: [null, [Validators.required, Validators.maxLength(255)]],
  });

  loading = signal(false);
  parameterId = signal<string | null>(null);

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.parameterId.set(id);
      this.loadData(id);
    }
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.parameterService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as Parameter;

        this.formGroup.patchValue({
          id: model.id,
          key: model.key,
          value: model.value,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar o parâmetro',
            life: 3000,
          });
        }
      },
    });
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const id = this.parameterId();
    if (!id) return;

    const value = this.formGroup.get('value')?.value as string;

    this.loading.set(true);

    let request$: Observable<ApiResult>;
    request$ = this.parameterService.updateValue(id, value);

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Parâmetro atualizado com sucesso',
          life: 2000,
        });

        this.router.navigate(['/parameters']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length === 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao atualizar o parâmetro',
              life: 3000,
            });
          }
        }
      },
    });
  }
}
